<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import Swal from 'sweetalert2'
import { isAdmin } from '@/composables/useAuth'

import { encodeId, decodeId } from '@/utils/obfuscate'

const isAdminUser = computed(() => isAdmin())

const route = useRoute()
const router = useRouter()
const id = Number(decodeId(route.params.id))

const roles = ref([])
const cargandoRoles = ref(false)
const errorRoles = ref('')

async function cargarRoles() {
  if (!isAdminUser.value) return
  cargandoRoles.value = true
  errorRoles.value = ''
  try {
    const { data } = await api.get('/roles')
    const lista = Array.isArray(data) ? data : (data.roles || data.data || [])
    roles.value = lista
  } catch (err) {
    errorRoles.value = err.response?.data?.message || err.message || 'No se pudieron cargar roles'
    Swal.fire('Error', errorRoles.value, 'error')
  } finally {
    cargandoRoles.value = false
  }
}
const GENEROS = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' },
    { value: 'Otro', label: 'Otro' }
]

const form = ref({
    id,
    nombre: '',
    identificacion: '',
    genero: '',
    email: '',
    password: '',        // en blanco = no cambiar
    telefono: '',
    direccion: '',
    fecha_nacimiento: '',
    rol_id: '',
    estado: 1
})

const loading = ref(false)
const errors = ref({})
const cargandoUsuario = ref(true)

// avatar
const avatarFile = ref(null)        // nuevo archivo
const preview = ref(null)           // preview del nuevo archivo
const currentAvatarUrl = ref(null)  // avatar actual del usuario

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp']
const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB

function validateImageFile(file) {
    if (!ALLOWED_MIME.includes(file.type)) {
        Swal.fire('Archivo no válido', 'Formato permitido: JPG, PNG o WEBP.', 'warning')
        return false
    }
    if (file.size > MAX_FILE_SIZE) {
        Swal.fire('Archivo muy grande', 'El tamaño máximo es 2 MB.', 'warning')
        return false
    }
    return true
}

function revokePreview() {
    if (preview.value && preview.value.startsWith('blob:')) {
        URL.revokeObjectURL(preview.value)
    }
}

function onFile(e) {
    const f = e.target.files?.[0]
    if (f && !validateImageFile(f)) {
        e.target.value = ''
        avatarFile.value = null
        revokePreview()
        preview.value = null
        return
    }
    revokePreview()
    avatarFile.value = f || null
    preview.value = f ? URL.createObjectURL(f) : null
}

onBeforeUnmount(() => {
    revokePreview()
})

function validate() {
    errors.value = {}
    if (!form.value.nombre?.trim()) errors.value.nombre = 'Requerido'
    if (!form.value.identificacion?.trim()) errors.value.identificacion = 'Requerido'
    if (isAdminUser.value && !form.value.rol_id) errors.value.rol_id = 'Seleccione un rol'
    if (form.value.email && form.value.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email.trim())) {
        errors.value.email = 'Email no válido'
    }
    return Object.keys(errors.value).length === 0
}

async function cargarUsuario() {
    try {
        cargandoUsuario.value = true
        const { data } = await api.get(`/users/${id}`)
        form.value = {
            id: data.id,
            nombre: data.nombre || '',
            identificacion: data.identificacion || '',
            genero: data.genero || '',
            email: data.email || '',
            password: '', // vacío para no tocarlo
            telefono: data.telefono || '',
            direccion: data.direccion || '',
            fecha_nacimiento: data.fecha_nacimiento || '',
            rol_id: data.rol_id ?? '',
            estado: Number(data.estado ?? 1)
        }
        currentAvatarUrl.value = data.avatar_url || null
        preview.value = null
        avatarFile.value = null
    } catch (err) {
        const msg = err.response?.data?.message || err.message
        Swal.fire('Error', msg || 'No se pudo cargar el usuario', 'error')
        router.push({ name: 'Socios' })
    } finally {
        cargandoUsuario.value = false
    }
}

async function cargarInicial() {
  if (isAdminUser.value) {
    await cargarRoles()
  }
  await cargarUsuario()
}

async function save({ goToMembership } = { goToMembership: false }) {
    if (!validate()) return
    try {
        loading.value = true

        // Construir payload sin rol_id para EMPLEADO (backend conserva SOCIO)
        const payload = {
            nombre: form.value.nombre,
            identificacion: form.value.identificacion,
            genero: form.value.genero,
            email: form.value.email,
            password: form.value.password,
            telefono: form.value.telefono,
            direccion: form.value.direccion,
            fecha_nacimiento: form.value.fecha_nacimiento,
            estado: form.value.estado,
        }
        if (isAdminUser.value && form.value.rol_id) {
            payload.rol_id = form.value.rol_id
        }
        const fd = new FormData()
        Object.entries(payload).forEach(([k, v]) => {
            if (v !== '' && v !== null && v !== undefined) fd.append(k, v)
        })
        // si password vacío, que no lo toque (ya filtrado por payload construcción, pero por compat)
        if (!payload.password) fd.delete('password')
        if (!payload.email) fd.delete('email')
        if (!payload.telefono) fd.delete('telefono')
        if (!payload.direccion) fd.delete('direccion')
        if (!payload.fecha_nacimiento) fd.delete('fecha_nacimiento')
        // archivo (opcional)
        if (avatarFile.value) fd.append('avatar', avatarFile.value)

        await api.put(`/users/${id}`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })

        Swal.fire({
            icon: 'success',
            title: 'Cambios guardados',
            timer: 1200,
            showConfirmButton: false
        })

        if (goToMembership) {
            router.push({ name: 'SocioDetalle', params: { id: encodeId(id) }, query: { nueva_membresia: 1 } })
        } else {
            router.push({ name: 'Socios' })
        }
    } catch (err) {
        const msg = err.response?.data?.message || err.message
        Swal.fire('Error', msg || 'No se pudo guardar', 'error')
    } finally {
        loading.value = false
    }
}

function cancel() {
    router.back()
}

onMounted(cargarInicial)
</script>

<template>
    <div class="container-fluid py-4">
        <div class="card p-4">
            <h5 class="mb-3">Editar Usuario </h5>

            <div v-if="cargandoUsuario" class="text-muted">Cargando...</div>

            <div v-else class="row g-3">
                <!-- Nombre -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Nombre *</label>
                    <input v-model="form.nombre" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.nombre }" />
                    <div class="invalid-feedback" v-if="errors.nombre">{{ errors.nombre }}</div>
                </div>

                <!-- Identificación -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Identificación *</label>
                    <input v-model="form.identificacion" type="text" class="form-control"
                        :class="{ 'is-invalid': errors.identificacion }" />
                    <div class="invalid-feedback" v-if="errors.identificacion">{{ errors.identificacion }}</div>
                </div>

                <!-- Género -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Género</label>
                    <select v-model="form.genero" class="form-select">
                        <option value="">— Seleccione —</option>
                        <option v-for="g in GENEROS" :key="g.value" :value="g.value">{{ g.label }}</option>
                    </select>
                </div>

                <!-- Rol -->
                <div v-if="isAdminUser" class="col-12 col-md-6">
                    <label class="form-label">Rol *</label>
                    <select v-model="form.rol_id" class="form-select" :class="{ 'is-invalid': errors.rol_id }" :disabled="cargandoRoles">
                        <option value="">— Seleccione —</option>
                        <option v-for="r in roles" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.rol_id">{{ errors.rol_id }}</div>
                    <small v-if="cargandoRoles" class="text-muted">Cargando roles...</small>
                    <small v-if="errorRoles" class="text-danger">{{ errorRoles }}</small>
                </div>
                <div v-else class="col-12 col-md-6">
                    <label class="form-label">Rol *</label>
                    <input type="text" class="form-control bg-light" value="SOCIO" readonly />
                    <small class="text-muted">No puedes cambiar el rol de un SOCIO.</small>
                </div>

                <!-- Email -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Email</label>
                    <input v-model="form.email" type="email" class="form-control" :class="{ 'is-invalid': errors.email }" />
                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                </div>

                <!-- Nueva contraseña -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Nueva contraseña</label>
                    <input v-model="form.password" type="password" class="form-control" autocomplete="new-password" />
                    <small class="text-muted">Deja en blanco para no cambiarla.</small>
                </div>

                <!-- Teléfono -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Teléfono</label>
                    <input v-model="form.telefono" type="text" class="form-control" />
                </div>

                <!-- Dirección -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Dirección</label>
                    <input v-model="form.direccion" type="text" class="form-control" />
                </div>

                <!-- Fecha nacimiento -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Fecha de nacimiento</label>
                    <input v-model="form.fecha_nacimiento" type="date" class="form-control" />
                </div>

                <!-- Estado -->
                <div class="col-12 col-md-6">
                    <label class="form-label">Estado</label>
                    <select v-model.number="form.estado" class="form-select">
                        <option :value="1">Activo</option>
                        <option :value="0">Inactivo</option>
                    </select>
                </div>

                <!-- Avatar -->
                <div class="col-12 col-md-6">
                    <label class="form-label d-block">Foto de perfil</label>

                    <div class="d-flex align-items-center gap-3 mb-2">
                        <div>
                            <div class="text-muted small mb-1">Actual</div>
                            <img v-if="currentAvatarUrl" :src="currentAvatarUrl" alt="actual"
                                style="width: 80px; height: 80px; object-fit: cover; border-radius: 10px;">
                            <div v-else class="text-muted small">Sin foto</div>
                        </div>

                        <div v-if="preview">
                            <div class="text-muted small mb-1">Nueva</div>
                            <img :src="preview" alt="preview"
                                style="width: 80px; height: 80px; object-fit: cover; border-radius: 10px;">
                        </div>
                    </div>

                    <input type="file" class="form-control" accept="image/jpeg,image/png,image/webp" @change="onFile" />
                    <small class="text-muted">JPG, PNG, WEBP máx. 2 MB.</small>
                </div>
            </div>

            <!-- acciones -->
            <div class="d-flex flex-wrap gap-2 justify-content-end mt-4">
                <button class="btn btn-light" :disabled="loading" @click="cancel">Cancelar</button>
                <button class="btn btn-secondary" :disabled="loading" @click="save({ goToMembership: false })">
                    Guardar
                </button>
                <button class="btn btn-primary" :disabled="loading" @click="save({ goToMembership: true })">
                    Guardar y asignar membresía
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.card {
    max-width: 1100px;
    margin-inline: auto;
}

img {
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
}
</style>
